import { useRef, useState, type FormEvent } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/shared/components/ui/button';
import { postSimulatorLead, statusOf } from './simulatorApi';

/**
 * The gate between the last question and the reports: the FP leaves an email,
 * saved as a sales lead, or skips. No consent box: the lead is the FP and the
 * answers are a simulation.
 *
 * `onSubmitted` fires only after the lead is saved, so a failed save is never
 * recorded as given. Skip never calls the endpoint and stays open after any
 * error; it is held only while a save is in flight, so a late 204 can't
 * overwrite the skip. Without a reCAPTCHA site key no lead can ever be saved
 * (the backend fails closed), so only Skip is offered.
 */

type GateError = 'invalid' | 'retry' | 'unavailable';

type SimulatorEmailGateProps = {
  onSubmitted: () => void;
  onSkip: () => void;
};

const getRecaptchaSiteKey = (): string | undefined =>
  import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() || undefined;

const toGateError = (error: unknown): GateError => {
  const status = statusOf(error);
  if (status === 400) return 'invalid';
  if (status === 404) return 'unavailable';
  return 'retry';
};

export function SimulatorEmailGate({ onSubmitted, onSkip }: SimulatorEmailGateProps) {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const siteKey = getRecaptchaSiteKey();
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<GateError | null>(null);

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // No widget or no token: the backend fails closed without one, so there
      // is nothing worth posting.
      const recaptchaToken = await recaptchaRef.current?.executeAsync();
      recaptchaRef.current?.reset();
      if (!recaptchaToken) throw new Error('reCAPTCHA gave no token');
      await postSimulatorLead({ email: email.trim(), recaptchaToken });
      onSubmitted();
    } catch (caught) {
      setError(toGateError(caught));
      setIsSaving(false);
    }
  };

  if (!siteKey) {
    return (
      <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <h1 className="font-serif text-2xl text-gray-900">Your reports are ready</h1>
        <p
          className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600"
          role="status"
        >
          Saving your email isn&apos;t available right now. You can skip to your reports.
        </p>
        <Button type="button" variant="outline" className="mt-6" onClick={onSkip}>
          Skip
        </Button>
      </div>
    );
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim() && !isSaving) void save();
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
    >
      <h1 className="text-center font-serif text-2xl text-gray-900">Your reports are ready</h1>
      <p className="mx-auto mt-3 max-w-md text-center text-[15px] leading-relaxed text-gray-600">
        Leave your email so the bQuest team can follow up with you, or skip straight to the reports.
      </p>

      <label htmlFor="simulator-email" className="mt-6 block text-sm font-medium text-gray-700">
        Email
      </label>
      <input
        id="simulator-email"
        type="email"
        autoComplete="email"
        // Same cap as the lead DTO (MAX_LEAD_EMAIL_LENGTH).
        maxLength={320}
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          if (error === 'invalid') setError(null);
        }}
        aria-invalid={error === 'invalid'}
        aria-describedby={error === 'invalid' ? 'simulator-email-error' : undefined}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-[15px] focus:border-[#026857] focus:outline-none focus:ring-1 focus:ring-[#026857] aria-[invalid=true]:border-red-500"
      />
      {error === 'invalid' && (
        <p id="simulator-email-error" className="mt-1 text-sm text-red-600" role="alert">
          We couldn&apos;t save that email. Check it and try again, or skip.
        </p>
      )}
      {error === 'retry' && (
        <div className="mt-4 text-center text-sm text-gray-600" role="alert">
          <p>Something went wrong saving your email.</p>
          <Button type="button" variant="outline" className="mt-2" onClick={() => void save()}>
            Try again
          </Button>
        </div>
      )}
      {error === 'unavailable' && (
        <p className="mt-4 text-center text-sm text-gray-600" role="alert">
          Saving your email isn&apos;t available right now. You can skip to your reports.
        </p>
      )}

      <div className="mt-6 flex justify-center gap-3">
        <Button type="button" variant="outline" onClick={onSkip} disabled={isSaving}>
          Skip
        </Button>
        <Button
          type="submit"
          className="bg-[#026857] hover:bg-[#025247]"
          disabled={!email.trim() || isSaving}
        >
          {isSaving ? 'Saving…' : 'See my reports'}
        </Button>
      </div>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />
    </form>
  );
}
